import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { apiFetch } from "../../../services/apiClient";
import { TabPanel } from "../../../components/layout";
import { LoadingState, EmptyState, ErrorBoundary } from "../../../components/ui";
import { useAuth } from "../../../components/context/AuthProvider";
import CampaignViewUsers from "./CampaignViewUsers";
import CampaignOverview from "./CampaignOverview";
import CampaignAssets from "./CampaignAssets";
import CampaignWorld from "./CampaignWorld";
import CampaignCharacters from "./CampaignCharacters";
import CampaignSessions from "./CampaignSessions";
import CampaignKnowledgeView from "./CampaignKnowledgeView";
import CampaignFateFormulas from "./CampaignFateFormulas";
import type { CampaignView } from "../../../types/types";

interface TabItem {
  id: string;
  label: string;
  badge?: number;
}

const CampaignViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [campaign, setCampaign] = useState<CampaignView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const activeTab = searchParams.get("tab") || "overview";

  const fetchCampaign = async () => {
    try {
      const response = await apiFetch(`/api/protected/campaigns/campaign/${id}`);

      if (response.ok) {
        const data = await response.json();
        setCampaign(data);
      } else {
        setError("Failed to fetch campaign details.");
      }
    } catch (err) {
      console.error("Error fetching campaign:", err);
      setError("An error occurred while fetching campaign details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaign();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleTabChange = (tab: string) => {
    setSearchParams({ tab });
  };

  if (loading) {
    return (
      <div className="bg-zinc-950 text-white min-h-screen flex items-center justify-center">
        <LoadingState variant="spinner" message="Loading campaign details..." size="lg" />
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="bg-zinc-950 text-white min-h-screen flex items-center justify-center">
        <EmptyState
          title="Error Loading Campaign"
          description={error || "Campaign not found."}
          icon="error"
          size="lg"
          action={
            <button
              onClick={fetchCampaign}
              className="px-4 py-2 bg-zinc-800 border border-zinc-700 text-white hover:bg-zinc-700 transition-colors"
            >
              Try Again
            </button>
          }
        />
      </div>
    );
  }

  const currentUserId = parseInt(auth.get("userId") ?? "0", 10);
  // ponytail: owner = creator; reuses same signal as CampaignAssets.tsx:127-128
  const canEdit = currentUserId === campaign.createdByUserId;

  const tabItems: TabItem[] = [
    { id: "overview", label: "Overview" },
    { id: "lore", label: "Lore" },
    { id: "sessions", label: "Sessions" },
    { id: "Heroes", label: "Characters", badge: campaign.characters?.length || 0 },
    { id: "assets", label: "Compendium" },
    { id: "world", label: "World" },
    { id: "knowledge", label: "Knowledge" },
    { id: "fate-formulas", label: "Fate Formulas" },
    { id: "users", label: "Users", badge: campaign.users?.length || 0 },
  ];

  const subtitle = [
    campaign.type,
    campaign.setting,
    campaign.world,
    campaign.isPublic ? "Public" : "Private",
  ]
    .filter(Boolean)
    .join(" • ");

  return (
    <ErrorBoundary
      errorMessage="Failed to render the campaign page. Please try refreshing the page."
      onError={(err) => console.error("Campaign page error:", err)}
    >
      <div className="bg-zinc-950 text-white min-h-screen">
        {/* Page Header */}
        <div className="px-6 py-4 border-b border-zinc-800">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-1.5 text-xs text-zinc-500 mb-1.5">
            <button
              onClick={() => navigate("/protected/campaigns")}
              className="hover:text-amber-400 transition-colors"
            >
              Chronicles
            </button>
            <span>›</span>
            <span className="text-zinc-400">{campaign.name}</span>
          </nav>

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-light tracking-wide">{campaign.name}</h1>
              <p className="text-sm text-zinc-400 mt-1">{subtitle}</p>
            </div>
            <div className="flex items-center gap-6 text-center">
              <div>
                <div className="text-xl font-light text-amber-400">
                  {campaign.users?.length || 0}
                </div>
                <div className="text-xs text-zinc-400 tracking-wide">Players</div>
              </div>
              {campaign.maxPlayers && (
                <div>
                  <div className="text-xl font-light text-amber-400">{campaign.maxPlayers}</div>
                  <div className="text-xs text-zinc-400 tracking-wide">Max Players</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabbed Content */}
        <div className="px-6 py-4">
          <div className="bg-zinc-900 border border-zinc-800">
            {/* Tab Bar */}
            <div className="flex border-b border-zinc-800 overflow-x-auto">
              {tabItems.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`px-5 py-3 text-sm whitespace-nowrap flex items-center gap-1.5 transition-colors ${
                    activeTab === tab.id
                      ? "text-amber-400 bg-amber-400/10 border-b-2 border-amber-400"
                      : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
                  }`}
                >
                  {tab.label}
                  {tab.badge !== undefined && tab.badge > 0 && (
                    <span className="text-xs bg-zinc-700 text-zinc-400 px-1.5 py-0.5">
                      {tab.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Tab Panels */}
            <div className="p-6">
              <TabPanel tabId="overview" isActive={activeTab === "overview"}>
                <ErrorBoundary>
                  <CampaignOverview campaign={campaign} />
                </ErrorBoundary>
              </TabPanel>

              <TabPanel tabId="lore" isActive={activeTab === "lore"}>
                <EmptyState
                  title="Lore Content"
                  description="Lore content coming soon..."
                  icon="info"
                  size="md"
                />
              </TabPanel>

              <TabPanel tabId="sessions" isActive={activeTab === "sessions"}>
                <ErrorBoundary>
                  <CampaignSessions campaign={campaign} onUpdate={fetchCampaign} />
                </ErrorBoundary>
              </TabPanel>

              <TabPanel tabId="Heroes" isActive={activeTab === "Heroes"}>
                <ErrorBoundary>
                  <CampaignCharacters campaign={campaign} onUpdate={fetchCampaign} />
                </ErrorBoundary>
              </TabPanel>

              <TabPanel tabId="assets" isActive={activeTab === "assets"}>
                <ErrorBoundary>
                  <CampaignAssets campaign={campaign} />
                </ErrorBoundary>
              </TabPanel>

              <TabPanel tabId="world" isActive={activeTab === "world"}>
                <ErrorBoundary>
                  <CampaignWorld campaignId={campaign.id} />
                </ErrorBoundary>
              </TabPanel>

              <TabPanel tabId="knowledge" isActive={activeTab === "knowledge"}>
                <ErrorBoundary>
                  <CampaignKnowledgeView campaign={campaign} />
                </ErrorBoundary>
              </TabPanel>

              <TabPanel tabId="fate-formulas" isActive={activeTab === "fate-formulas"}>
                <ErrorBoundary>
                  <CampaignFateFormulas campaignId={campaign.id} canEdit={canEdit} />
                </ErrorBoundary>
              </TabPanel>

              <TabPanel tabId="users" isActive={activeTab === "users"}>
                <ErrorBoundary>
                  <CampaignViewUsers campaign={campaign} onUpdate={fetchCampaign} />
                </ErrorBoundary>
              </TabPanel>
            </div>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default CampaignViewPage;
